import { getT } from '@/i18n';
import { useT, useUnitSystem } from '@/i18n/react';
import { formatShortDistance } from '@/lib/format';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { doc, setDoc } from 'firebase/firestore';
import { useAuth } from '@/hooks/AuthProvider';
import { useProfile } from '@/hooks/ProfileProvider';
import { db } from '@/lib/firebase';
import { api, ApiError } from '@/lib/api';
import { Button, SegmentedControl, Switch, WheelPicker } from '@/components/ui';
import { Icon } from '@/components/Icon';
import './PrivacyZoneOnboardingSheet.css';

type Radius = '50' | '100' | '200';

const RADII = [
  { value: '50', get label() { return formatShortDistance(50); } },
  { value: '100', get label() { return formatShortDistance(100); } },
  { value: '200', get label() { return formatShortDistance(200); } },
] as const;

/**
 * Az onboarding kötelező lépése: „Alapadatok" (átugorható) + „Privát zóna"
 * (átugorhatatlan) — `docs/02-funkcionalis-spec.md` → Onboarding 4–5. pont.
 *
 * ⚠️ A GATE-PONT SZÁNDÉKOSAN NEM A TELJES APP, HANEM AZ ELSŐ RÖGZÍTÉS-INDÍTÁS
 * (Geri döntése, GRUNDO #65): élesben már vannak felhasználók, akiknél
 * `privacyZoneSetAt` null, mert ez a lépés még sosem futott le. Egy teljes
 * app-zárolás őket kész funkciók elől zárná el meglepetésszerűen. A `Dock`
 * ezt a lapot nyitja meg a rögzítés-indító gomb (a visszaszámlálás) elé,
 * ha a profil `privacy.privacyZoneSetAt` még `null`.
 *
 * A kör itt ILLUSZTRÁCIÓ, nem élő térkép — a valódi Mapbox-előnézet (a
 * spec makettjében szereplő „térkép a védőkörrel") későbbi kiegészítés
 * lehet, ha Geri ragaszkodik a pontos makett-egyezéshez.
 */
export function PrivacyZoneOnboardingSheet({ onComplete }: { onComplete: () => void }) {
  const t = useT();
  const units = useUnitSystem();
  const { user } = useAuth();
  const { profile, patchProfile } = useProfile();
  const [step, setStep] = useState<'alapadatok' | 'privatzona'>('alapadatok');

  const [hasWeight, setHasWeight] = useState(false);
  const [hasHeight, setHasHeight] = useState(false);
  const weightFactor = units === 'imperial' ? 0.45359237 : 1;
  const heightFactor = units === 'imperial' ? 2.54 : 1;
  const [weightKg, setWeightKg] = useState(72);
  const [heightCm, setHeightCm] = useState(175);

  const [hideStart, setHideStart] = useState(true);
  const [startRadius, setStartRadius] = useState<Radius>('200');
  const [hideEnd, setHideEnd] = useState(true);
  const [endRadius, setEndRadius] = useState<Radius>('200');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const maxRadius = Math.max(hideStart ? Number(startRadius) : 0, hideEnd ? Number(endRadius) : 0);

  async function saveBodyAndContinue() {
    if (user && db && (hasWeight || hasHeight)) {
      try {
        const body = { ...(profile?.body ?? {}) } as Record<string, unknown>;
        if (hasWeight) body.weightKg = Math.round(weightKg * 10) / 10;
        if (hasHeight) body.heightCm = Math.round(heightCm);
        await setDoc(doc(db, 'users', user.uid), { body, updatedAt: new Date() }, { merge: true });
        patchProfile({ body: body as never });
      } catch {
        // Az alapadatok átugorhatók — egy elmaradt mentés nem állíthatja meg az onboardingot.
      }
    }
    setStep('privatzona');
  }

  async function confirmPrivacyZone() {
    setSaving(true);
    setError('');
    try {
      const result = await api.updateActivityPrivacy({
        hideStart,
        startRadiusM: Number(startRadius) as 50 | 100 | 200,
        hideEnd,
        endRadiusM: Number(endRadius) as 50 | 100 | 200,
      });
      patchProfile({ privacy: { ...profile?.privacy, ...result.privacy } as never });
      onComplete();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : getT().privacyOnboarding.failed);
    } finally {
      setSaving(false);
    }
  }

  return createPortal(
    <div className="pzgate" role="dialog" aria-modal="true">
      <div className="pzgate__sheet">
        {step === 'alapadatok' ? (
          <>
            <h1 className="pzgate__title">{t.privacyOnboarding.basics}</h1>
            <p className="pzgate__lead">
              {t.privacyOnboarding.intro}
            </p>
            {hasWeight ? (
              <WheelPicker
                aria-label={t.privacyOnboarding.weight}
                min={Math.ceil(30 / weightFactor)}
                max={Math.floor(200 / weightFactor)}
                step={0.5}
                majorEvery={10}
                value={weightKg / weightFactor}
                onChange={(value) => setWeightKg(value * weightFactor)}
                unitLabel={units === 'imperial' ? 'lb' : 'kg'}
              />
            ) : (
              <Button variant="secondary" onClick={() => setHasWeight(true)}>
                {t.privacyOnboarding.enterWeight}
              </Button>
            )}
            {hasHeight ? (
              <WheelPicker
                aria-label={t.privacyOnboarding.height}
                min={Math.ceil(100 / heightFactor)}
                max={Math.floor(220 / heightFactor)}
                step={1}
                majorEvery={10}
                value={heightCm / heightFactor}
                onChange={(value) => setHeightCm(value * heightFactor)}
                unitLabel={units === 'imperial' ? 'in' : 'cm'}
              />
            ) : (
              <Button variant="secondary" onClick={() => setHasHeight(true)}>
                {t.privacyOnboarding.enterHeight}
              </Button>
            )}
            <div className="pzgate__actions">
              <Button variant="secondary" onClick={() => setStep('privatzona')}>
                {t.privacyOnboarding.skip}
              </Button>
              <Button onClick={() => void saveBodyAndContinue()}>{t.privacyOnboarding.next}</Button>
            </div>
          </>
        ) : (
          <>
            <h1 className="pzgate__title">{t.privacyOnboarding.title}</h1>
            <p className="pzgate__lead">
              {t.privacyOnboarding.privacyHint}
            </p>

            <div className="pzgate__circle-wrap" aria-hidden="true">
              <svg viewBox="0 0 200 200" className="pzgate__circle-svg">
                <circle
                  cx="100"
                  cy="100"
                  r={maxRadius > 0 ? Math.min(90, 30 + maxRadius / 4) : 6}
                  className="pzgate__circle-ring"
                  style={{ opacity: maxRadius > 0 ? 1 : 0 }}
                />
                <circle cx="100" cy="100" r="6" className="pzgate__circle-dot" />
              </svg>
              <span key={maxRadius} className="pzgate__circle-label pzgate__circle-label--pop">
                {maxRadius > 0 ? formatShortDistance(maxRadius) : t.privacyOnboarding.noHiding}
              </span>
            </div>

            <PrivacyZoneRow
              icon="eye-off"
              title={t.privacyOnboarding.start}
              checked={hideStart}
              onChecked={setHideStart}
              radius={startRadius}
              onRadius={setStartRadius}
              disabled={saving}
            />
            <PrivacyZoneRow
              icon="eye-off"
              title={t.privacyOnboarding.end}
              checked={hideEnd}
              onChecked={setHideEnd}
              radius={endRadius}
              onRadius={setEndRadius}
              disabled={saving}
            />

            {error ? <p className="field__error" role="alert">{error}</p> : null}
            <div className="pzgate__actions">
              <Button block loading={saving} onClick={() => void confirmPrivacyZone()}>
                {t.privacyOnboarding.ok}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

function PrivacyZoneRow({
  icon,
  title,
  checked,
  onChecked,
  radius,
  onRadius,
  disabled,
}: {
  icon: 'eye-off';
  title: string;
  checked: boolean;
  onChecked: (value: boolean) => void;
  radius: Radius;
  onRadius: (value: Radius) => void;
  disabled: boolean;
}) {
  const t = useT();
  useUnitSystem();
  return (
    <section className="pzgate__zone card">
      <div className="pzgate__zone-head">
        <Icon name={icon} size={20} />
        <Switch checked={checked} onChange={onChecked} label={t.activityPrivacy.hide(title)} disabled={disabled} />
      </div>
      {checked ? (
        <SegmentedControl
          options={RADII}
          value={radius}
          onChange={onRadius}
          label={t.activityPrivacy.zoneRadius(title)}
          block
          size="sm"
        />
      ) : null}
    </section>
  );
}
